import Styles from './CaptionTab.module.scss'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { TabPanel } from '../TabPanel/TabPanel'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { useEffect, useMemo, useState } from 'react'
import { AI, Edit1 } from 'blaze-ui/icons'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { CaptionWriteYourOwn } from './CaptionWriteYourOwn'
import { CaptionGenerateWithAI } from './CaptionGenerateWithAI'
import { CaptionsStore } from 'stores/CaptionsStore'
import { CaptionEdit } from './CaptionEdit'
import { Item } from '@react-stately/collections'
import { useOpenBrandKitCreationModal } from 'hooks/useOpenBrandKitCreationModal'
import { DesignChannel } from 'components/NewFileModal/Types'

interface Props {
  captionsStore: CaptionsStore
  customPrompt: string
  setCustomPrompt: (prompt: string) => void
  voice?: number
  tones: Tone[]
  language: string
  selectVoice: (voice?: number) => void
  selectTone: (tone: Tone) => void
  setLanguage: (language: string) => void
  generate: () => void
  insertCaption: (caption: string) => void
  removeCaption: () => void
}

export const CaptionGenerationForm = observer(
  ({
    captionsStore,
    customPrompt,
    setCustomPrompt,
    voice,
    tones,
    language,
    selectVoice,
    selectTone,
    setLanguage,
    generate,
    insertCaption,
    removeCaption,
  }: Props) => {
    const { designStore, drawersStore } = useRootStore()

    const channel = designStore?.file?.channel as DesignChannel | undefined

    const caption = captionsStore.caption

    const [manualCaption, setManualCaption] = useState(caption)
    const [mode, setMode] = useState<'ai' | 'write'>('ai')
    const [regenerating, setRegenerating] = useState(false)

    const { data: allBrandKits, isLoading } = useBrandKits()

    const brandKits = useMemo(() => allBrandKits?.filter((bk) => !!bk.brand_voice) ?? [], [allBrandKits])

    const { openBrandKitCreationModal } = useOpenBrandKitCreationModal({ newStack: true })

    useEffect(() => {
      if (isLoading || mode === 'write' || voice || tones.length > 0) {
        return
      }

      const kitVoice = brandKits[0]?.brand_voice
      if (kitVoice) {
        selectVoice(kitVoice.id)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandKits, voice, tones, mode, isLoading, caption])

    const openAddBrandKitModal = () => {
      drawersStore.closeAllDrawers()
      openBrandKitCreationModal()
    }

    const renderContent = () => {
      if (caption && !regenerating) {
        return (
          <CaptionEdit
            caption={manualCaption}
            setCaption={setManualCaption}
            insertCaption={() => insertCaption(manualCaption)}
            removeCaption={removeCaption}
            setRegenerating={setRegenerating}
            channel={channel}
          />
        )
      }

      return (
        <>
          <div className={Styles.toggle}>
            <SegmentedControl
              className={Styles.toggleElement}
              onItemClick={(name) => setMode(name as 'ai' | 'write')}
              activeItem={mode}
              variant="secondary"
              rounded
            >
              <Item key="ai" textValue="Generate">
                <AI />
                Generate
              </Item>
              <Item key="write" textValue="Write Your Own">
                <Edit1 />
                Write Your Own
              </Item>
            </SegmentedControl>
          </div>

          {mode === 'write' ? (
            <CaptionWriteYourOwn insertCaption={insertCaption} />
          ) : (
            <CaptionGenerateWithAI
              customPrompt={customPrompt}
              setCustomPrompt={setCustomPrompt}
              brandKits={brandKits}
              voice={voice}
              tones={tones}
              language={language}
              setLanguage={setLanguage}
              selectTone={selectTone}
              selectVoice={selectVoice}
              openAddBrandKitModal={openAddBrandKitModal}
              onSubmit={generate}
            />
          )}
        </>
      )
    }

    return (
      <TabPanel.Root>
        <TabPanel.Header
          onBack={() => designStore?.openDefaultTab()}
          title={captionsStore?.sectionTitle ?? 'Caption'}
        />
        <TabPanel.Body>{renderContent()}</TabPanel.Body>
      </TabPanel.Root>
    )
  },
)
